import React, { useEffect, useRef, useState } from 'react';
import {
  FaUsers,
  FaBolt,
  FaTachometerAlt,
  FaExclamationCircle,
  FaSearch,
  FaInfoCircle,
  FaCreditCard,
  FaArrowRight
} from "react-icons/fa";

const useInView = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const current = ref.current;

    if (!current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(current);
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
    };
  }, []);

  return [ref, isVisible];
};

const QuickLinkCard = ({ item, index }) => {
  const [ref, isVisible] = useInView();

  return (
    <div
      ref={ref}
      className={`
        break-inside-avoid group relative flex flex-col rounded-[2.5rem] p-5 shadow-lg 
        hover:shadow-2xl transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]
        border border-gray-100 transform hover:-translate-y-2
        ${item.bgColor} ${item.textColor}
        ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}
      `}
      style={{
        transitionDelay: `${index * 100}ms`,
        willChange: "transform, opacity"
      }}
    >
      <div className="w-14 h-14 rounded-full border border-current flex items-center justify-center mb-6 opacity-90">
        <span className="text-2xl">{item.icon}</span>
      </div>
      <h3 className="font-bold text-xl mb-4 leading-snug">
        {item.title}
      </h3>
      <p className="text-sm leading-relaxed mb-8 opacity-90">
        {item.description}
      </p>
      <div className="mt-auto">
        <a
          href={item.link}
          className="inline-flex items-center gap-2 font-semibold py-3 px-5 rounded-full border border-current hover:bg-black hover:text-white transition-all duration-300"
        >
          {item.buttonText}
          <FaArrowRight className="transition-transform group-hover:translate-x-2" />
        </a>
      </div>

      <div className="absolute bottom-4 right-6 opacity-10 pointer-events-none">
        <FaBolt size={70} />
      </div>
    </div>
  );
};

const QuickLinks = () => {
  const linksData = [
    {
      title: "New Account Creation",
      icon: <FaCreditCard />,
      description: "Register for a new postpaid account easily.",
      buttonText: "Create Account",
      link: "https://newsetup.beninelectric.com",
      bgColor: "bg-[#14532d]",
      textColor: "text-white"
    },
    {
      title: "Online Payment",
      icon: <FaCreditCard />,
      description: "Pay your prepaid and postpaid bills online instantly.",
      buttonText: "Pay Now",
      link: "/e-payment-channels",
      bgColor: "bg-[#166534]",
      textColor: "text-white"
    },
    {
      title: "Contractors' Portal",
      icon: <FaUsers />,
      description: "Manage contractor profiles and access documents.",
      buttonText: "Access Portal",
      link: "https://netapp.beninelectric.com",
      bgColor: "bg-[#14532d]",
      textColor: "text-white"
    },
    {
      title: "Order a Meter",
      icon: <FaTachometerAlt />,
      description: "Request a new meter and track your application.",
      buttonText: "Order Now",
      link: "https://imaap.beninelectric.com",
      bgColor: "bg-[#facc15]",
      textColor: "text-black"
    },
    {
      title: "Quick Complaints",
      icon: <FaExclamationCircle />,
      description: "Report outages and issues instantly.",
      buttonText: "Report Issue",
      link: "https://crm.beninelectric.com/complaints",
      bgColor: "bg-[#052e16]",
      textColor: "text-white"
    },
    {
      title: "Track Complaints",
      icon: <FaSearch />,
      description: "Track your complaint status easily.",
      buttonText: "Track Now",
      link: "https://crm.beninelectric.com/",
      bgColor: "bg-[#fefce8]",
      textColor: "text-black"
    },
    {
      title: "Feeder Information",
      icon: <FaInfoCircle />,
      description: "Check feeder updates in your area.",
      buttonText: "View Details",
      link: "/customer-feeder-info",
      bgColor: "bg-[#dcfce7]",
      textColor: "text-black"
    },
    {
      title: "MAP Meter Refund",
      icon: <FaBolt />,
      description: "Get refund tokens for your MAP meters.",
      buttonText: "Claim Refund",
      link: "/map-meter-refund",
      bgColor: "bg-[#15803d]",
      textColor: "text-white"
    },
    {
      title: "FAQs",
      icon: <FaInfoCircle />,
      description: "Find answers to frequently asked questions.",
      buttonText: "View FAQs",
      link: "/faq",
      bgColor: "bg-[#166534]",
      textColor: "text-white"
    }
  ];

  return (
    <div className="relative w-full  bg-white -mt-1 overflow-hidden min-h-screen">
      <div
        className="absolute inset-0 z-0 pointer-events-none bg-cover bg-center opacity-10"
        style={{
          backgroundImage: "url('/images/background-2.jpg')",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, black 25%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0%, black 25%)"
        }}
      />

      <div className="container relative z-10 mx-auto px-6">
        <h2 className="text-3xl font-black text-black text-center mb-8 uppercase tracking-tight">
          Customer Service Hub
        </h2>

        <div className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8">
          {linksData.map((item, index) => (
            <QuickLinkCard key={index} item={item} index={index} />
          ))}
        </div>

      </div>
    </div>
  );
};

export default QuickLinks;